export type BootStatus = 'loading' | 'ready' | 'stalled'

export interface BootWatchHandlers {
  /** Called whenever the boot status changes. */
  onStatus: (status: BootStatus) => void
  /** Called once when the boot stalls (watchdog timeout or rejection), with a reason for telemetry. */
  onStall?: (reason: string) => void
}

/**
 * Guards the app boot, which is gated on a single Medplum auth/profile call
 * (`getProfileAsync` → `GET /auth/me`) that has no built-in timeout. Races that
 * call against a watchdog: if it hasn't resolved within `watchdogMs`, or it
 * rejects, the status becomes `'stalled'` so the UI can offer a retry instead of
 * spinning on the loading indicator forever. If the call later resolves on its
 * own (e.g. a slow-but-eventually-successful request), the status becomes
 * `'ready'` and the app proceeds — so a transient network stall no longer wedges
 * the boot.
 *
 * Framework-agnostic so the timing logic is unit-testable without a DOM.
 *
 * @param ready - resolves when the app may proceed past the loading screen.
 * @param watchdogMs - show the stalled state if `ready` hasn't settled by then.
 * @param handlers - status/stall callbacks.
 * @param handlers.onStatus - called whenever the boot status changes.
 * @param handlers.onStall - called once when the boot stalls, with a reason for telemetry.
 * @returns a `cancel` function that stops further status emissions and clears the
 *   watchdog timer. Call it from effect cleanup.
 */
export function createBootWatch(
  ready: Promise<unknown>,
  watchdogMs: number,
  { onStatus, onStall }: BootWatchHandlers
): () => void {
  let cancelled = false
  let settled = false
  let stalled = false

  const watchdog = setTimeout(() => {
    if (cancelled || settled) {
      return
    }
    stalled = true
    onStall?.('watchdog-timeout')
    onStatus('stalled')
  }, watchdogMs)

  ready
    .then(() => {
      settled = true
      clearTimeout(watchdog)
      // A slow-but-eventually-successful call recovers the boot even after the
      // watchdog already showed the stalled state.
      if (!cancelled) {
        onStatus('ready')
      }
    })
    .catch((cause: unknown) => {
      settled = true
      clearTimeout(watchdog)
      // Don't re-emit stall telemetry if the watchdog already reported it; the
      // rejection is just the same stalled call finally failing.
      if (cancelled || stalled) {
        return
      }
      onStall?.(cause instanceof Error ? cause.message : String(cause))
      onStatus('stalled')
    })

  return () => {
    cancelled = true
    clearTimeout(watchdog)
  }
}
